import { LogOut, Menu, Search } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '#client/providers/AuthProvider';
import { Button } from '#client/components/ui/Button';
import { ThemeToggle } from '#client/components/ui/ThemeToggle';
import { SignOutDialog } from '#client/features/finance';
import { can } from '#client/lib/permissions';
import { useCommandPalette } from '#client/features/command-palette';
import { personInitials } from '#shared/types';

interface TopBarProps {
  onToggleNavigation: () => void;
}

export function TopBar({ onToggleNavigation }: TopBarProps) {
  const { user, signOut } = useAuth();
  const palette = useCommandPalette();
  const [isClosingDay, setIsClosingDay] = useState(false);
  // Whoever takes money closes their day on the way out; the bench just leaves.
  const closesDay = can(user, 'dayClose.submit');

  const initials = user ? personInitials(user) : '';

  return (
    <header className="c-topbar">
      <button
        type="button"
        className="c-topbar__nav-toggle"
        onClick={onToggleNavigation}
        aria-label="Toggle navigation"
      >
        <Menu aria-hidden="true" />
      </button>

      {/* The shortcut is the fast path; this exists so it is discoverable at
          all — a keyboard-only feature nobody knows about is not a feature. */}
      <button type="button" className="c-topbar__search" onClick={palette.open}>
        <Search aria-hidden="true" />
        Search patients, PIN, screens…
        <kbd className="c-topbar__search-hint">Ctrl K</kbd>
      </button>

      <div className="c-topbar__spacer" />

      <ThemeToggle />

      {user && (
        <div className="c-topbar__user">
          <span className="c-topbar__avatar" aria-hidden="true">
            {initials || '?'}
          </span>
          <span className="c-topbar__identity">
            <span className="c-topbar__name">
              {user.first_name} {user.last_name ?? ''}
            </span>
            <span className="c-topbar__role">{user.role.name}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            icon={<LogOut />}
            onClick={() => (closesDay ? setIsClosingDay(true) : void signOut())}
          >
            Sign out
          </Button>
        </div>
      )}
      {isClosingDay && <SignOutDialog onClose={() => setIsClosingDay(false)} />}
    </header>
  );
}
